/*
 * Effective permissions — current user's resolved permission set + the
 * platform/workspace roles that produced it. Mirrors the data
 * usePlatformPermissions / useWorkspacePermissions consume internally
 * but renders it as a verifiable surface for support staff.
 */

import { useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import { api, ApiError } from "../lib/api.js";
import { SkeletonTable } from "../components/Skeleton.js";

type Catalog = Awaited<ReturnType<typeof api.rbacPermissions>>;

export default function EffectivePermissions() {
  const [me, setMe] = useState<Awaited<ReturnType<typeof api.platformMe>> | null>(null);
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [scopeFilter, setScopeFilter] = useState<"all" | "platform" | "workspace">("all");

  useEffect(() => {
    Promise.all([api.platformMe(), api.rbacPermissions()])
      .then(([m, c]) => { setMe(m); setCatalog(c); })
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }, []);

  const granted = useMemo(() => new Set(me?.permissions ?? []), [me]);
  const rows = useMemo(() => {
    if (!catalog) return [];
    let r = catalog.permissions;
    if (scopeFilter !== "all") r = r.filter((p) => p.scope === scopeFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter((p) => p.key.toLowerCase().includes(q) || p.name.toLowerCase().includes(q));
    }
    return r;
  }, [catalog, scopeFilter, search]);

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Effective permissions</h1>
          <p className="muted">
            What the current user can actually do, resolved across every role assignment.
            Useful for support: "this user says they can't do X" → look here first.
          </p>
        </div>
      </header>

      {error && <div className="ts-alert ts-alert-error">{error}</div>}

      {me && (
        <section className="empty-state-card" style={{ marginBottom: 16 }}>
          <h2 style={{ marginTop: 0 }}>Identity</h2>
          <dl className="dl-grid">
            <dt>User ID</dt><dd className="mono">{me.userId ?? "—"}</dd>
            <dt>Default role</dt><dd>{me.role}</dd>
            <dt>Workspace</dt><dd className="mono">{me.workspaceId ?? "—"}</dd>
            <dt>Platform roles</dt>
            <dd>{me.platformRoles.length === 0 ? <span className="muted">none</span> : me.platformRoles.map((r) => <span key={r} className="pill pill-info" style={{ marginRight: 4 }}>{r}</span>)}</dd>
            <dt>Workspace roles</dt>
            <dd>
              {Object.entries(me.workspaceRoles ?? {}).length === 0
                ? <span className="muted">none</span>
                : Object.entries(me.workspaceRoles).map(([wid, roles]) => (
                    <div key={wid} style={{ marginBottom: 4 }}>
                      <code style={{ fontSize: 11 }}>{wid}</code>:{" "}
                      {(roles as string[]).map((r) => <span key={r} className="pill pill-muted" style={{ marginRight: 4 }}>{r}</span>)}
                    </div>
                  ))}
            </dd>
            <dt>Permissions held</dt><dd>{me.permissions.length}</dd>
          </dl>
        </section>
      )}

      <section className="transfer-filters">
        <label className="transfer-filter">
          Scope
          <select value={scopeFilter} onChange={(e) => setScopeFilter(e.target.value as typeof scopeFilter)}>
            <option value="all">All</option>
            <option value="platform">Platform</option>
            <option value="workspace">Workspace</option>
          </select>
        </label>
        <label className="transfer-filter transfer-filter-grow">
          Search
          <input type="search" placeholder="key / name…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
      </section>

      {!catalog && <SkeletonTable rows={6} columns={4} />}
      {catalog && rows.length > 0 && (
        <table className="transfer-table">
          <thead>
            <tr>
              <th>Permission</th>
              <th>Scope</th>
              <th>Category</th>
              <th>Granted?</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => {
              const has = granted.has(p.key);
              return (
                <tr key={p.key}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{p.name}</div>
                    <code style={{ fontSize: 11 }}>{p.key}</code>
                  </td>
                  <td><span className="pill pill-muted">{p.scope}</span></td>
                  <td className="muted" style={{ fontSize: 12 }}>{p.category}</td>
                  <td>{has ? <span className="pill pill-ok">yes</span> : <span className="pill pill-muted">no</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
